import React, { useState } from 'react';
import { X, Star, Filter, Play, Phone, ChevronLeft, Linkedin, Link2 } from 'lucide-react';
import { VideoModal } from '@clareefai/ui';
import type { PublicVendorReview } from '@clareefai/types';
import { PUBLIC_SITE_URL } from '../../utils/publicSite';

interface VideosDialogProps {
  isOpen: boolean;
  onClose: () => void;
  videos: PublicVendorReview[];
  companyName: string;
  pageSlug?: string;
  onRequestCall?: (reviewId: string, promoterId: string, promoterName: string) => void;
}

const Initials: React.FC<{ name: string; className?: string }> = ({
  name,
  className = 'w-10 h-10 text-sm',
}) => (
  <div
    className={`${className} bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0`}
  >
    {name.split(' ').map((n) => n[0]).join('').substring(0, 2).toUpperCase()}
  </div>
);

const COMPANY_SIZE_OPTIONS = ['1-10', '11-50', '51-200', '201-500', '501-1000', '1000+'];
const INDUSTRY_OPTIONS = ['Technology', 'Healthcare', 'Finance', 'Education', 'Retail', 'Manufacturing'];

const VideosDialog: React.FC<VideosDialogProps> = ({
  isOpen,
  onClose,
  videos,
  companyName,
  pageSlug,
  onRequestCall,
}) => {
  const [selectedVideo, setSelectedVideo] = useState<PublicVendorReview | null>(null);
  const [playingVideoUrl, setPlayingVideoUrl] = useState<string | null>(null);
  const [copiedLink, setCopiedLink] = useState(false);
  const [filterCompanySize, setFilterCompanySize] = useState('');
  const [filterIndustry, setFilterIndustry] = useState('');
  const [filterProduct, setFilterProduct] = useState('');

  if (!isOpen) return null;

  const handleShareLinkedIn = () => {
    const url = pageSlug
      ? `${PUBLIC_SITE_URL}/company/${pageSlug}`
      : window.location.href;
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, '_blank');
  };

  const handleCopyLink = () => {
    const url = pageSlug
      ? `${PUBLIC_SITE_URL}/company/${pageSlug}`
      : window.location.href;
    navigator.clipboard.writeText(url);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  return (
    <div className="fixed inset-0 z-50 bg-white overflow-y-auto">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          {selectedVideo ? (
            <button
              onClick={() => setSelectedVideo(null)}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
              <span className="text-2xl font-bold text-gray-900">Back to All Videos</span>
            </button>
          ) : (
            <h2 className="text-2xl font-bold text-gray-900">All Video Testimonials</h2>
          )}
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-6 h-6 text-gray-500" />
          </button>
        </div>

        {/* Filters */}
        {!selectedVideo && (
          <div className="flex flex-wrap gap-3 mb-8">
            <div className="relative">
              <select
                value={filterCompanySize}
                onChange={(e) => setFilterCompanySize(e.target.value)}
                className="appearance-none pl-9 pr-8 py-2 border border-gray-200 rounded-full text-sm bg-white hover:border-gray-300 transition-colors cursor-pointer"
              >
                <option value="">Company Size</option>
                {COMPANY_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            </div>
            <div className="relative">
              <select
                value={filterIndustry}
                onChange={(e) => setFilterIndustry(e.target.value)}
                className="appearance-none pl-9 pr-8 py-2 border border-gray-200 rounded-full text-sm bg-white hover:border-gray-300 transition-colors cursor-pointer"
              >
                <option value="">Industry</option>
                {INDUSTRY_OPTIONS.map((ind) => (
                  <option key={ind} value={ind}>{ind}</option>
                ))}
              </select>
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            </div>
            <div className="relative">
              <select
                value={filterProduct}
                onChange={(e) => setFilterProduct(e.target.value)}
                className="appearance-none pl-9 pr-8 py-2 border border-gray-200 rounded-full text-sm bg-white hover:border-gray-300 transition-colors cursor-pointer"
              >
                <option value="">Product</option>
              </select>
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            </div>
          </div>
        )}

        {/* Detail view */}
        {selectedVideo ? (
          <div className="max-w-3xl mx-auto">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              {selectedVideo.videoUrl && (
                <div
                  className="relative aspect-video bg-gradient-to-br from-slate-900 to-indigo-900 overflow-hidden cursor-pointer group"
                  onClick={() => setPlayingVideoUrl(selectedVideo.videoUrl!)}
                >
                  <video
                    src={`${selectedVideo.videoUrl}#t=0.1`}
                    preload="metadata"
                    muted
                    playsInline
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/30" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                      <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-lg">
                        <Play className="w-6 h-6 text-blue-600 ml-0.5" />
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div className="p-8">
                <div className="flex items-center gap-4 mb-4">
                  <Initials name={selectedVideo.promoterName} className="w-12 h-12 text-base" />
                  <div>
                    <p className="font-semibold text-gray-900">{selectedVideo.promoterName}</p>
                    {selectedVideo.promoterCompany && (
                      <p className="text-sm text-gray-500">{selectedVideo.promoterCompany}</p>
                    )}
                  </div>
                  {selectedVideo.rating && (
                    <div className="ml-auto flex items-center gap-1 px-2.5 py-1 bg-yellow-50 rounded-full">
                      <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                      <span className="text-sm font-semibold text-yellow-700">{selectedVideo.rating}.0</span>
                    </div>
                  )}
                </div>
                {selectedVideo.comment && (
                  <p className="text-gray-700 leading-relaxed mb-6">"{selectedVideo.comment}"</p>
                )}
                <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
                  <button
                    onClick={handleShareLinkedIn}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-[#0A66C2] text-white rounded-lg text-sm font-medium hover:bg-[#004182] transition-colors"
                  >
                    <Linkedin className="w-4 h-4" />
                    Share on LinkedIn
                  </button>
                  <button
                    onClick={handleCopyLink}
                    className="inline-flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <Link2 className="w-4 h-4" />
                    {copiedLink ? 'Copied!' : 'Copy Link'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Grid view */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {videos.map((video) => (
              <div
                key={video.id}
                className="bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-md hover:border-blue-100 transition-all"
              >
                {/* Thumbnail */}
                <div
                  className="relative aspect-video bg-gradient-to-br from-slate-800 to-indigo-900 overflow-hidden cursor-pointer group"
                  onClick={() => setPlayingVideoUrl(video.videoUrl!)}
                >
                  <video
                    src={`${video.videoUrl}#t=0.1`}
                    preload="metadata"
                    muted
                    playsInline
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/30" />
                  {video.rating && (
                    <div className="absolute top-3 left-3 flex items-center gap-1 px-2 py-0.5 bg-yellow-400 rounded-md z-10">
                      <Star className="w-3 h-3 text-white fill-white" />
                      <span className="text-xs font-bold text-white">{video.rating}.0</span>
                    </div>
                  )}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-14 h-14 bg-blue-600 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg">
                      <Play className="w-6 h-6 text-white ml-0.5" />
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <Initials name={video.promoterName} className="w-9 h-9 text-xs" />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{video.promoterName}</p>
                      {video.promoterCompany && (
                        <p className="text-xs text-gray-500 truncate">{video.promoterCompany}</p>
                      )}
                    </div>
                  </div>
                  {video.comment && (
                    <p className="text-sm text-gray-600 line-clamp-3 leading-relaxed mb-3">
                      "{video.comment}"
                    </p>
                  )}
                  <div className="flex items-center gap-1.5 text-xs text-gray-400 mb-4">
                    <span>Review from</span>
                    <div className="w-4 h-4 bg-blue-600 rounded-full flex items-center justify-center">
                      <span className="text-white text-[8px] font-bold">C</span>
                    </div>
                  </div>
                  {onRequestCall && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onRequestCall(video.id, video.promoterId, video.promoterName);
                      }}
                      className="w-full flex items-center justify-center gap-2 py-2.5 border-2 border-orange-500 text-orange-600 rounded-xl text-sm font-medium hover:bg-orange-50 transition-colors"
                    >
                      <Phone className="w-4 h-4" />
                      Request a one-on-one call
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <VideoModal
        isOpen={!!playingVideoUrl}
        onClose={() => setPlayingVideoUrl(null)}
        videoUrl={playingVideoUrl || ''}
      />
    </div>
  );
};

export default VideosDialog;
